import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';

export type CustomerTier = 'gold' | 'silver' | 'bronze';

export type MobileSession = {
  email: string;
  token: string;
  refreshToken: string;
  tokenExpiry: number;
  refreshTokenExpiry: number;
  customerTier?: CustomerTier;
};

const SESSION_KEY = 'bgi_mobile_session';

type AuthResponse = {
  token?: string;
  token_expiry?: number;
  refresh_token?: string;
  refresh_token_expiry?: number;
  error?: string;
};

type DryStackResponse<T> = {
  success?: boolean;
  data?: T;
  messages?: string[];
};

type CustomerProfileResponse = {
  rank?: string;
};

@Injectable({ providedIn: 'root' })
export class MobileSessionService {
  private readonly http = inject(HttpClient);
  private readonly apiBaseURL = this.resolveAPIBaseURL(environment.apiBaseUrl);
  private readonly authURL = `${this.apiBaseURL}/api/v1/drystack/keymaker/authenticate/email`;
  private readonly profileURL = `${this.apiBaseURL}/api/v1/bg-imports/customer/me`;
  private readonly sessionState = signal<MobileSession | null>(this.readSession());

  public readonly session = computed<MobileSession | null>(() => this.sessionState());
  public readonly isLoggedIn = computed<boolean>(() => this.sessionState() !== null);

  public async login(email: string, password: string): Promise<{ ok: boolean; message?: string }> {
    const normalizedEmail = String(email || '').trim().toLowerCase();
    const normalizedPassword = String(password || '').trim();

    if (!normalizedEmail || !normalizedPassword) {
      return { ok: false, message: 'Email and password are required.' };
    }

    try {
      const response = await firstValueFrom(
        this.http.post<AuthResponse>(this.authURL, {
          email: normalizedEmail,
          password: normalizedPassword,
        }),
      );

      const responseError = String(response?.error ?? '').trim();
      if (responseError) {
        return { ok: false, message: responseError };
      }

      const token = String(response?.token ?? '').trim();
      const refreshToken = String(response?.refresh_token ?? '').trim();
      const tokenExpiry = Number(response?.token_expiry ?? 0);
      const refreshTokenExpiry = Number(response?.refresh_token_expiry ?? 0);

      if (!token || !refreshToken || !tokenExpiry || !refreshTokenExpiry) {
        return { ok: false, message: 'Authentication failed.' };
      }

      const payload: MobileSession = {
        email: normalizedEmail,
        token,
        refreshToken,
        tokenExpiry,
        refreshTokenExpiry,
      };

      this.sessionState.set(payload);
      localStorage.setItem(SESSION_KEY, JSON.stringify(payload));
      return { ok: true };
    } catch (error: any) {
      const status = Number(error?.status ?? 0);
      const messageFromBody =
        String(error?.error?.error || '').trim() ||
        String(error?.error?.message || '').trim() ||
        String(error?.message || '').trim();

      if (status === 401) {
        return { ok: false, message: messageFromBody || 'Incorrect email or password.' };
      }

      if (status === 0) {
        return { ok: false, message: 'Unable to reach login server. Please check your connection and try again.' };
      }

      const message = messageFromBody || 'Invalid email or password.';

      return { ok: false, message };
    }
  }

  public logout(): void {
    this.sessionState.set(null);
    localStorage.removeItem(SESSION_KEY);
  }

  public async loadCustomerTier(forceRefresh = false): Promise<CustomerTier> {
    const activeSession = this.sessionState();
    if (!activeSession?.token) {
      return 'bronze';
    }

    if (!forceRefresh && activeSession.customerTier) {
      return activeSession.customerTier;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${activeSession.token}`,
      'x-request-id': this.requestID(),
    });

    try {
      const response = await firstValueFrom(
        this.http.get<DryStackResponse<CustomerProfileResponse>>(this.profileURL, { headers }),
      );

      const rawRank = String(response?.data?.rank || '').trim().toLowerCase();
      const tier = this.normalizeTier(rawRank);
      this.sessionState.update((session) => {
        if (!session) {
          return session;
        }

        const updated = { ...session, customerTier: tier };
        localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
        return updated;
      });

      return tier;
    } catch {
      return 'bronze';
    }
  }

  private readSession(): MobileSession | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<MobileSession>;
      if (!parsed || !parsed.email || !parsed.token || !parsed.refreshToken) {
        return null;
      }

      const tokenExpiry = Number(parsed.tokenExpiry ?? 0);
      const refreshTokenExpiry = Number(parsed.refreshTokenExpiry ?? 0);
      if (!tokenExpiry || !refreshTokenExpiry) {
        return null;
      }

      const nowSeconds = Math.floor(Date.now() / 1000);
      if (nowSeconds >= refreshTokenExpiry) {
        return null;
      }

      return {
        email: String(parsed.email).trim().toLowerCase(),
        token: String(parsed.token).trim(),
        refreshToken: String(parsed.refreshToken).trim(),
        tokenExpiry,
        refreshTokenExpiry,
        customerTier: this.parseStoredTier(parsed.customerTier),
      };
    } catch {
      return null;
    }
  }

  private normalizeTier(rank: string): CustomerTier {
    if (rank === 'gold' || rank === 'silver' || rank === 'bronze') {
      return rank;
    }

    return 'bronze';
  }

  private parseStoredTier(value: unknown): CustomerTier | undefined {
    const rank = String(value || '').trim().toLowerCase();
    if (rank === 'gold' || rank === 'silver' || rank === 'bronze') {
      return rank;
    }

    return undefined;
  }

  private requestID(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `req-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
  }

  private resolveAPIBaseURL(url: string): string {
    return String(url || '').replace(/\/+$/, '');
  }
}
